import { useTranslation } from "react-i18next";
import { AgreementAttachmentsDisplay } from "./AgreementAttachmentsDisplay";
import { AgreementDataRowLabelValue } from "./AgreementDataRowLabelValue";
import { FileMetadata } from "../../utils";
import { cn } from "@/lib/utils";
import type { AgreementField } from "@/types/agreement";

export type { AgreementField, AgreementFields } from "@/types/agreement";
interface AgreementDataRowProps {
  field: AgreementField;
  draftValue: unknown;
  confirmedValue: unknown;
  translationPrefix: string;
  attachments?: Record<string, FileMetadata[]>;
  draftData?: Record<string, unknown>;
  confirmedData?: Record<string, unknown>;
}

const oddEvenBg = "odd:bg-white even:bg-gray-100/60";

/**
 * Treats null and false as equivalent (same from business perspective).
 * Backend may differ during initialization.
 */
const normalizeEmptyValue = (value: unknown): unknown => {
  if (value === null || value === false) {
    return false;
  }
  return value;
};

export const AgreementDataRow = ({
  field,
  draftValue,
  confirmedValue,
  translationPrefix,
  attachments,
  draftData = {},
  confirmedData = {},
}: AgreementDataRowProps) => {
  const { t } = useTranslation();

  const normalizedDraftValue = normalizeEmptyValue(draftValue);
  const normalizedConfirmedValue = normalizeEmptyValue(confirmedValue);
  const hasDifferences = normalizedDraftValue !== normalizedConfirmedValue;

  if (field.type === "attachments") {
    if (!attachments) return null;
    const attachmentList = attachments[field.key] || [];

    return (
      <tr className={oddEvenBg}>
        <td colSpan={4} className="px-2 py-1 border border-gray-200/60">
          <AgreementAttachmentsDisplay
            attachments={attachmentList}
            title={t(`agreements.attachments.${field.key}`)}
            attachmentType={field.key}
          />
        </td>
      </tr>
    );
  }

  return (
    <tr
      className={cn(
        oddEvenBg,
        "hover:bg-blue-100/40 transition-all duration-150 border border-gray-200/60",
      )}
    >
      <AgreementDataRowLabelValue
        translationPrefix={translationPrefix}
        fieldKey={field.key}
        field={field}
        value={draftValue}
        data={draftData}
        hasHighlight={hasDifferences}
      />
      <AgreementDataRowLabelValue
        translationPrefix={translationPrefix}
        fieldKey={field.key}
        field={field}
        value={confirmedValue}
        data={confirmedData}
        hasHighlight={hasDifferences}
      />
    </tr>
  );
};
